import { mesclarConfigRemota, resolverConfig } from "./config";
import { ConsoleBuffer } from "./capture/console";
import { buscarConfigRemota } from "./intake/configRemota";
import { WidgetUI } from "./ui/widget";
import { apontarHotkey, ligarHotkey } from "./ui/hotkey";
import type { AltBugConfig } from "./types";

/**
 * Entrada do widget (bundle IIFE → global `AltBug`). Auto-inicializa a partir dos
 * `data-*` do próprio <script> se um token estiver presente; senão espera
 * `AltBug.init({ token, ... })`. Idempotente.
 */

/** <script> do widget, capturado no LOAD (document.currentScript só é confiável
    durante a execução síncrona do bundle; depois, no init() manual, seria null). */
function acharScript(): HTMLScriptElement | null {
  return document.querySelector<HTMLScriptElement>("script[data-altbug-token]");
}
const SCRIPT: HTMLScriptElement | null =
  (document.currentScript as HTMLScriptElement | null) ?? acharScript();

let ui: WidgetUI | null = null;

/* A hotkey liga AQUI, no escopo de módulo — antes do bootstrap e antes do
   `await buscarConfigRemota`. É a diferença entre ganhar do host e depender de
   um round-trip de rede terminar: quem registra em window/captura mais cedo
   ganha de quem registra depois, e o alvo entra quando o widget existir. */
ligarHotkey();
let consoleBuffer: ConsoleBuffer | null = null;
let consoleAtivo = false;
let iniciando = false;
let geracao = 0; // invalida uma inicialização assíncrona em curso se destruir/reiniciar

function bootstrap(override?: Partial<AltBugConfig>): void {
  if (ui || iniciando) return; // já inicializado (ou inicializando)
  // endpoint deriva da origem do próprio <script src> quando o data-attr é omitido.
  const cfg = resolverConfig(SCRIPT?.dataset, override, SCRIPT?.src);
  if (!cfg.token) {
    console.warn("[AltBug] token ausente — configure data-altbug-token ou AltBug.init({ token }).");
    return;
  }
  if (!cfg.endpoint) {
    console.warn("[AltBug] endpoint não resolvido — sirva o bundle pelo backend ou passe data-altbug-endpoint.");
    return;
  }

  iniciando = true;
  // Captura de console começa cedo (default local, "desde o load"); a config do
  // servidor pode desligá-la logo abaixo, antes de qualquer envio.
  consoleBuffer = new ConsoleBuffer(cfg.consoleMaxLinhas);
  if (cfg.consoleHabilitado) {
    consoleBuffer.iniciar();
    consoleAtivo = true;
  }
  void iniciar(cfg, ++geracao);
}

/** Busca a config do relator na API (ou a última boa, do cache) e só então monta a UI —
    evita "flash" de cor/ícone, e desde o DEV-144 é daqui que vêm também os rótulos e os
    tipos do 2º item do menu. */
async function iniciar(cfg: AltBugConfig, ger: number): Promise<void> {
  const remota = await buscarConfigRemota(cfg.endpoint, cfg.token);
  if (ger !== geracao) return; // destruído/reiniciado durante o fetch
  if (remota) mesclarConfigRemota(cfg, remota);

  // Alinha a captura de console ao valor final (o servidor pode ter mudado).
  if (cfg.consoleHabilitado && !consoleAtivo) {
    consoleBuffer!.iniciar();
    consoleAtivo = true;
  } else if (!cfg.consoleHabilitado && consoleAtivo) {
    consoleBuffer!.destruir(); // para de capturar e descarta o que já pegou
    consoleBuffer = new ConsoleBuffer(cfg.consoleMaxLinhas);
    consoleAtivo = false;
  }

  const montar = () => {
    if (ger !== geracao) return;
    ui = new WidgetUI(cfg, consoleBuffer!);
    apontarHotkey(ui, cfg.hotkeyCombo);
    iniciando = false;
  };
  if (document.body) montar();
  else window.addEventListener("DOMContentLoaded", montar, { once: true });
}

export const init = (override?: Partial<AltBugConfig>): void => bootstrap(override);
export const abrir = (): void => ui?.abrir();
/** Abre o painel de suporte (menu + "Meus chamados") — o que o botão flutuante faz.
    Existe separado de `abrir()` para o host que esconde o FAB e usa botão próprio: ali
    `abrir()` continua indo direto para a captura, como sempre foi. */
export const chamados = (): void => ui?.abrirPainel();
export const fechar = (): void => ui?.fechar();
export const destruir = (): void => {
  geracao++; // invalida qualquer iniciar() em voo
  iniciando = false;
  ui?.destruir();
  ui = null;
  apontarHotkey(null);
  consoleBuffer?.destruir();
  consoleBuffer = null;
  consoleAtivo = false;
};
export const versao = "0.1.0";

// Auto-init se o <script> já traz o token (uso mais comum: cola-e-esquece).
if (SCRIPT?.dataset.altbugToken) bootstrap();
